import { replyAwaitingInput } from "@/api/awaiting-input";
import { getDurableConversation } from "@/api/conversations";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { selectConversationAsInputTarget } from "@/hooks/use-conversation-select";
import { captureInputTargetRoute } from "@/lib/input-target-route-guard";
import { useCanvasStore, type AwaitingInput } from "@/stores/canvas-store";
import { useInputBarQuestionStore } from "@/stores/input-bar-question-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { useProjectStore } from "@/stores/project-store";
import { submitQuestionAttachments } from "./submit-question-attachments";
import type { ScreenRecordingsContext } from "./screen-recordings-context";

export interface InputBarQuestion {
  awaiting: AwaitingInput;
  /** 1-based place among the frame's pending questions, oldest first. */
  position: number;
  total: number;
  /** Other known pending questions on the same frame, retained through loading. */
  remaining?: AwaitingInput[];
}

function frameQuestions(
  awaitingInputs: ReadonlyMap<string, AwaitingInput[]>,
  frameId: string,
): AwaitingInput[] {
  return [...(awaitingInputs.get(frameId) ?? [])].sort(
    (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
  );
}

export function inputBarQuestionFor(
  awaitingInputs: ReadonlyMap<string, AwaitingInput[]>,
  awaitingInputId: string,
  saved: InputBarQuestion | null = useInputBarQuestionStore.getState().question,
): InputBarQuestion | null {
  for (const [frameId, list] of awaitingInputs) {
    if (!list.some((item) => item.id === awaitingInputId)) continue;
    const questions = frameQuestions(awaitingInputs, frameId);
    const index = questions.findIndex((item) => item.id === awaitingInputId);
    return {
      awaiting: questions[index]!,
      position: index + 1,
      total: questions.length,
      remaining: questions.filter((item) => item.id !== awaitingInputId),
    };
  }
  return (
    pendingReplies.get(awaitingInputId) ??
    (saved?.awaiting.id === awaitingInputId ? saved : null)
  );
}

function summonMainInputBar(): void {
  useCanvasStore.getState().setPendingCursorSummon({
    x: window.innerWidth / 2,
    y: Math.max(40, window.innerHeight - 80),
    displayId: getWindowDisplayId(),
    placement: "bottom",
  });
}

/** Answer (spec §5.4): question mode for this question, the main bar
 *  summoned and, when the question has a conversation, that conversation
 *  targeted through the select-conversation flow. */
export async function answerInInputBar(awaitingInputId: string): Promise<void> {
  const question = inputBarQuestionFor(
    useCanvasStore.getState().awaitingInputs,
    awaitingInputId,
  );
  if (!question) return;
  useInputBarQuestionStore.getState().start(awaitingInputId, question);
  // The card replies through replyAwaitingInput whatever the target is.
  await openConversationInInputBar(
    question.awaiting.conversationId,
    () =>
      useInputBarQuestionStore.getState().awaitingInputId === awaitingInputId,
  );
}

/** Summon the main input bar and, when `conversationId` is set, target that
 *  conversation through the select-conversation flow while `stillWanted`.
 *  A conversation that can't be targeted (a native sub-agent, a closed
 *  chat) leaves the current target in place. */
export async function openConversationInInputBar(
  conversationId: string | null,
  stillWanted: () => boolean = () => true,
): Promise<void> {
  summonMainInputBar();
  if (!conversationId) return;
  if (useInputBarTarget.getState().target?.conversationId === conversationId) {
    return;
  }
  const route = captureInputTargetRoute();
  try {
    const conversation = await getDurableConversation(conversationId);
    if (!stillWanted() || !route.routeIsCurrent()) return;
    await selectConversationAsInputTarget(conversation, {
      isCurrentScope: () => stillWanted() && route.isCurrentScope(),
      routeIsCurrent: () => stillWanted() && route.routeIsCurrent(),
    });
  } catch {
    // Keep the current target.
  }
}

/** After a reply: drop the question locally (the resolved broadcast may lag)
 *  and show the frame's next question, or leave question mode. */
export function advanceInputBarQuestion(answered: AwaitingInput): void {
  const selected = useInputBarQuestionStore.getState();
  const active = selected.awaitingInputId === answered.id;
  const retained = selected.question?.remaining ?? [];
  useCanvasStore.getState().removeAwaitingInput(answered.id);
  if (!active) return;
  // Live entries supersede their retained versions. A temporary project clear
  // must not discard the rest of the frame's already known question queue.
  const candidates = new Map(retained.map((item) => [item.id, item]));
  for (const item of frameQuestions(
    useCanvasStore.getState().awaitingInputs,
    answered.frameId,
  ))
    candidates.set(item.id, item);
  candidates.delete(answered.id);
  const ordered = [...candidates.values()].sort(
    (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
  );
  const [next, ...remaining] = ordered;
  const questionMode = useInputBarQuestionStore.getState();
  if (next)
    questionMode.start(next.id, {
      awaiting: next,
      position: 1,
      total: ordered.length,
      remaining,
    });
  else questionMode.finish();
}

const pendingReplies = new Map<string, InputBarQuestion>();

export function isInputBarQuestionReplyPending(id: string): boolean {
  return pendingReplies.has(id);
}

/** The one way a typed answer leaves question mode: replyAwaitingInput, never
 *  a prompt, so follow-up routing can't answer the question a second time. */
export async function replyToInputBarQuestion(
  awaiting: AwaitingInput,
  answer: string,
  selectedOption?: string,
  imageIds: string[] = [],
  screenRecordings?: ScreenRecordingsContext,
): Promise<boolean> {
  const projectId =
    awaiting.projectId ?? useProjectStore.getState().currentProject?.id;
  if (
    !projectId ||
    (!answer.trim() && !imageIds.length && !screenRecordings?.clips.length) ||
    pendingReplies.has(awaiting.id) ||
    useInputBarQuestionStore.getState().replyingId === awaiting.id
  )
    return false;
  pendingReplies.set(
    awaiting.id,
    inputBarQuestionFor(
      useCanvasStore.getState().awaitingInputs,
      awaiting.id,
    ) ?? { awaiting, position: 1, total: 1 },
  );
  useInputBarQuestionStore.getState().setReplying(awaiting.id);
  let replied = false;
  try {
    if (imageIds.length || screenRecordings?.clips.length) {
      replied = await submitQuestionAttachments({
        projectId,
        conversationId: awaiting.conversationId,
        executionId: awaiting.executionId,
        awaitingInputId: awaiting.id,
        text: answer,
        imageIds,
        screenRecordings,
      });
    } else {
      await replyAwaitingInput(projectId, awaiting.id, {
        answer,
        ...(selectedOption !== undefined ? { selectedOption } : {}),
      });
      replied = true;
    }
  } catch {
    // A resolved event is authoritative even if the HTTP acknowledgement failed.
    replied =
      useInputBarQuestionStore.getState().awaitingInputId === awaiting.id &&
      useInputBarQuestionStore.getState().resolved;
  } finally {
    pendingReplies.delete(awaiting.id);
  }
  if (replied) advanceInputBarQuestion(awaiting);
  if (useInputBarQuestionStore.getState().replyingId === awaiting.id)
    useInputBarQuestionStore.getState().setReplying(null);
  return replied;
}
